import request from "@/apis/axios-instance";
import { Dimension } from "@/types/mapping";
import {
  AddLineItemsRequest,
  AddTagRequest,
  AxisParentContextItemsResponse,
  CreateExtendedTagRequest,
  CreateExtendedTagResponse,
  GetTagDetailResponse,
  PostSimilarTagsResponse,
  RoleListResponse,
  TagSearchResultsResponseV2,
  TagUsageInfoResponse,
} from "./types";

export const getTagUsageInfo = async (workbookId: string, tagKey: string) => {
  const response = await request.get(
    `/v1/workbooks/${workbookId}/taxonomy/tags/${tagKey}/usage-info`,
  );

  return response.data as TagUsageInfoResponse;
};

export const getRoleInfo = async (
  workbookId: string,
  roleKey: string,
): Promise<RoleListResponse> => {
  const response = await request.get(
    `/v1/workbooks/${workbookId}/taxonomy/roles/${roleKey}`,
  );
  return response.data as RoleListResponse;
};

export const getRoleList = async (
  workbookId: string,
  usedOnly: boolean = false,
) => {
  const response = await request.get(
    `/v1/workbooks/${workbookId}/taxonomy/roles?usedOnly=${usedOnly}`,
  );

  return response.data as RoleListResponse;
};

export const getAxisTagsInRole = async (
  workbookId: string,
  roleKey: string,
  tableId: string,
): Promise<AxisParentContextItemsResponse> => {
  const response = await request.get(
    `/v1/workbooks/${workbookId}/taxonomy/roles/${roleKey}/axes`,
    {
      params: {
        tableId,
      },
    },
  );
  return response.data as AxisParentContextItemsResponse;
};

export const getTagDetail = async (
  workbookId: string,
  tableId?: string,
  tagKey?: string,
  roleKey?: string,
): Promise<GetTagDetailResponse> => {
  if (!tagKey) {
    throw new Error("tagKey is required");
  }

  if (!tableId) {
    throw new Error("tableId is required");
  }

  try {
    const response = await request.get(
      `/v1/workbooks/${workbookId}/taxonomy/tags/${tagKey}`,
      {
        params: {
          tableId,
          roleKey: roleKey === "NONE" ? null : roleKey,
        },
      },
    );

    return {
      detail: response.data,
    };
  } catch (error) {
    throw new Error("Failed to fetch from server");
  }
};

export const getTagSearchResultsV2 = async (
  workbookId: string,
  query: string,
  tableId: string,
  dimension: Dimension,
) => {
  try {
    const response = await request.get(
      `/v2/workbooks/${workbookId}/taxonomy/tags`,
      {
        params: {
          query,
          dimension,
          tableId,
        },
      },
    );
    return response.data as TagSearchResultsResponseV2;
  } catch (error) {
    return {
      tags: [],
    };
  }
};

export const selectAxis = async (
  workbookId: string,
  roleKey: string,
  body: AddTagRequest,
) => {
  try {
    const response = await request.post(
      `/v1/workbooks/${workbookId}/taxonomy/roles/${roleKey}/axes`,
      body,
    );

    return response.data;
  } catch (error) {
    return [];
  }
};

export const selectLineItem = async (
  workbookId: string,
  roleKey: string,
  body: AddLineItemsRequest,
) => {
  try {
    const response = await request.post(
      `/v1/workbooks/${workbookId}/taxonomy/roles/${roleKey}/line-items`,
      body,
    );
    return response.data;
  } catch (error) {
    return [];
  }
};

export const createExtendedTag = async (
  workbookId: string,
  body: CreateExtendedTagRequest,
): Promise<CreateExtendedTagResponse> => {
  const response = await request.post(
    `/v1/workbooks/${workbookId}/taxonomy/tags`,
    body,
  );
  return response.data;
};

export const updateTaxonomyTagCache = async ({
  workbookId,
  tableId,
  address,
  dimension,
  tagKey,
}: {
  workbookId: string;
  tableId: string;
  address: string;
  dimension: Dimension;
  tagKey: string;
}) => {
  try {
    const response = await request.patch(
      `/v1/workbooks/${workbookId}/tables/${tableId}/taxonomy/tags`,
      {
        address,
        dimension,
        extendedTag: {
          key: tagKey,
        },
      },
    );
    return {
      status: 200,
      description: "Success",
    };
  } catch (error) {
    return {
      status: 500,
      description: "Failed to fetch from server",
    };
  }
};

export const postSimilarTags = async (
  workbookId: string,
  koLabel: string,
  dimension: Dimension,
) => {
  const response = await request.post(
    `/v1/workbooks/${workbookId}/taxonomy/tags/ai-search`,
    {
      koLabel,
      dimension,
    },
  );
  return response.data as PostSimilarTagsResponse;
};
